import { DndContext, type DragEndEvent, DragOverlay } from '@dnd-kit/core';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import {
	getFetchAllGroupQueryKey,
	useFetchAllGroupSuspense,
	useUpdateGroup,
} from '@uturn/api/v2';
import { Permission } from '@uturn/portal/abac';
import { LoadingSpan } from '@uturn/portal/components';
import { Page } from '@uturn/portal/components/page';
import { sonner } from '@uturn/ui-kit';
import { t } from 'i18next';
import { Suspense, useState, useTransition } from 'react';
import { useAbac } from 'react-abac';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import Groups from '../components/groups/groups';
import { DragItem } from '../components/Item';
import List from '../components/list/list';
import { GroupProvider, useGroupContext } from '../context/group-context';

type Carrier = {
	organizationId: number;
	organizationName: string;
	email: string;
	isPending?: boolean;
};

export type TGroup = {
	groupId: number;
	groupName: string;
	carriers?: Carrier[];
	isPending?: boolean;
};

export function CarriersOverview() {
	const { userHasPermissions } = useAbac();
	const { t } = useTranslation();
	const router = useRouter();
	const canInviteCarriers = userHasPermissions(Permission.INVITE_CARRIER);
	const { data: groups } = useFetchAllGroupSuspense({
		query: {
			select: (data) => data.data,
		},
	});

	const addCarrierClick = () => {
		if (userHasPermissions(Permission.INVITE_CARRIER)) {
			return router.navigate({ to: '/carriers/add' });
		}
		return sonner.error(
			t(
				'pages.carriers.actions-denied.add.description',
				'Cannot invite contract carrier while your organisation is pending approval by UTURN.',
			),
		);
	};

	if (!canInviteCarriers) {
		return (
			<Page title={t('pages.carriers.pagetitle', 'Carriers').toString()}>
				<p>
					{t(
						'pages.carriers.access-denied',
						'You do not have permission to view carriers.',
					)}
				</p>
			</Page>
		);
	}

	return (
		<>
			<Helmet title={t('pages.carriers.pagetitle', 'My Carriers').toString()} />
			<Page
				title={t('pages.carriers.pagetitle', 'Carriers').toString()}
				primaryAction={{
					label: t('pages.carriers.actions.add', 'Add carrier'),
					onClick: addCarrierClick,
				}}
			>
				{canInviteCarriers && (
					<Suspense fallback={<LoadingSpan />}>
						<GroupProvider initialGroups={groups}>
							<LabeledCarriers />
						</GroupProvider>
					</Suspense>
				)}
			</Page>
		</>
	);
}

const LabeledCarriers = () => {
	const queryClient = useQueryClient();
	const { setGroups, optimisticGroups, setOptimisticGroups } =
		useGroupContext();
	const [_, startTransition] = useTransition();
	const { mutateAsync: mutateUpdateGroup } = useUpdateGroup();

	// This function can probably be optimized and might be a little verbose
	const handleDragEnd = ({ active, over }: DragEndEvent) => {
		// If there is no target column or the target column is the same as the origin column, exit early
		if (!over || active.data.current?.groupId === over.id) {
			return;
		}

		const targetGroup = optimisticGroups.find(
			(item) => item.groupId === over.id,
		);
		if (!targetGroup) {
			return;
		}

		// Check if member already exists in target group
		const memberExists = targetGroup.carriers?.some(
			(carrier) => carrier.organizationId === active.data.current?.actualId,
		);
		if (memberExists) {
			sonner.error(
				t(
					'move-carrier.toast.error-message',
					'Failed to add carrier. Carrier already exists in "{{name}}"',
					{
						name: targetGroup.groupName,
					},
				),
			);
			return;
		}

		// Can possibly be adjuste dot use a typeguard instead
		const carrierId = active.data.current?.actualId;
		const carrierTitle = active.data.current?.title ?? active.id;
		const carrierSubtitle = active.data.current?.subtitle ?? active.id;
		const originGroupId = active.data.current?.groupId;

		// If dragging from another group, optimistically remove from origin group
		if (originGroupId) {
			const originGroup = optimisticGroups.find(
				(item) => item.groupId === originGroupId,
			);

			if (originGroup) {
				setOptimisticGroups({
					type: 'REMOVE_CARRIER_FROM_GROUP',
					carrierId,
					groupId: originGroup.groupId,
				});

				startTransition(async () => {
					// Update origin group in backend
					try {
						await mutateUpdateGroup(
							{
								id: originGroup.groupId,
								data: {
									carrierIds:
										originGroup.carriers
											?.filter(
												(carrier) => carrier.organizationId !== carrierId,
											)
											.map((carrier) => carrier.organizationId) ?? [],
									groupName: originGroup.groupName,
								},
							},
							{
								onError: () => {
									sonner.error(
										t(
											'remove-carrier.toast.error-message',
											'Failed to remove carrier from "{{name}}"',
											{
												name: originGroup.groupName,
											},
										),
									);
									queryClient.invalidateQueries({
										queryKey: getFetchAllGroupQueryKey(),
									});
								},
							},
						);

						setGroups((state) => {
							const groupIdx = state.findIndex(
								(g) => g.groupId === originGroup.groupId,
							);
							if (groupIdx === -1) {
								return state;
							}

							const result = [...state];
							result[groupIdx] = {
								...originGroup,
								carriers: originGroup.carriers?.filter(
									(carrier) => carrier.organizationId !== carrierId,
								),
							};

							return result;
						});
					} catch (error) {
						console.error(error);
					}
				});
			}
		}

		setOptimisticGroups({
			type: 'ADD_CARRIER_TO_GROUP',
			carrier: {
				id: carrierId,
				title: carrierTitle,
				subtitle: carrierSubtitle,
			},
			groupId: targetGroup.groupId,
		});

		// Optimistically add to target group
		startTransition(async () => {
			// Update target group in backend
			try {
				await mutateUpdateGroup(
					{
						id: targetGroup.groupId,
						data: {
							carrierIds: [
								...(targetGroup.carriers?.map(
									(carrier) => carrier.organizationId,
								) ?? []),
								carrierId,
							],
							groupName: targetGroup.groupName,
						},
					},
					{
						onSuccess: async () => {
							sonner.success(
								t(
									'add-carrier.toast.success-message',
									'Successfully added carrier to "{{name}}"',
									{
										name: targetGroup.groupName,
									},
								),
							);
							await queryClient.invalidateQueries({
								queryKey: getFetchAllGroupQueryKey(),
							});
						},
						onError: () => {
							sonner.error(
								t(
									'add-carrier.toast.error-message',
									'Failed to add carrier to "{{name}}"',
									{
										name: targetGroup.groupName,
									},
								),
							);
						},
					},
				);

				setGroups((state) => {
					const targetGroupIdx = state.findIndex(
						(group) => group.groupId === targetGroup.groupId,
					);
					if (targetGroupIdx === -1) {
						return state;
					}

					const result = [...state];

					// Check if carrier already exists
					if (
						targetGroup.carriers?.some(
							(carrier) => carrier.organizationId === carrierId,
						)
					) {
						return state;
					}

					result[targetGroupIdx] = {
						...targetGroup,
						carriers: [
							...(targetGroup.carriers ?? []),
							{
								organizationId: carrierId,
								organizationName: carrierTitle,
								email: carrierSubtitle,
							},
						],
					};

					return result;
				});
			} catch (error) {
				console.error(error);
			}
		});
	};

	return (
		<div>
			<DndContext onDragEnd={handleDragEnd}>
				<>
					<DragItem />
					<div className="flex gap-10">
						<Groups />
						<List />
					</div>
				</>
			</DndContext>
		</div>
	);
};
